import { apiDelete, apiGet, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import { formatCurrency, formatDate } from '@/shared/formatters';
import type { StatusCumprimento } from '@/types/cumprimento/cumprimentoCoreTypes';
import type {
  Anotacao,
  AtividadeRecente,
  CumprimentoDetalhes,
  TimelineItemData,
} from '@/types/cumprimento/cumprimentoTypes';

interface AtualizarStatusDto {
  status: string;
  observacoes?: string;
}

interface ArquivarCumprimentoDto {
  motivo: string;
  dataArquivamento: string;
  observacoes?: string;
}

interface CriarAnotacaoDto {
  texto: string;
  prazo?: string;
  responsavel?: string[];
  andamento?: string;
  prioridade?: 'alta' | 'media' | 'baixa';
}

interface AtualizarAnotacaoDto extends CriarAnotacaoDto {
  status?: 'ativa' | 'concluida' | 'cancelada';
}

interface AnotacaoBackend {
  id: string;
  texto: string;
  prazo?: string;
  responsaveis: string[];
  andamento?: string;
  status: 'ATIVA' | 'CONCLUIDA' | 'CANCELADA';
  prioridade?: 'BAIXA' | 'MEDIA' | 'ALTA';
  dataCriacao: string;
  dataAtualizacao: string;
  dataConclusao?: string;
  usuario?: {
    nomeCompleto: string;
    email: string;
    fotoPerfil?: string;
  };
}

interface AtividadeBackend {
  id: string;
  execucaoId: string;
  usuarioId: string;
  tipoAtividade: string;
  descricao: string;
  dadosAnteriores?: Record<string, unknown>;
  dadosNovos?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  dataAtividade: string;
  usuario?: {
    id: string;
    nomeCompleto: string;
    email: string;
    fotoPerfil?: string;
  };
}

interface ParteExecucao {
  id: string;
  tipoParte: 'AUTOR' | 'REU' | 'TERCEIRO';
  pessoa?: {
    id: string;
    nomeCompleto: string;
    cpfCnpj?: string;
    email?: string;
    telefone?: string;
  };
  nomeCompleto?: string;
  documentoPrincipal?: string;
  documentoFormatado?: string;
  dadosContato?: {
    email?: string;
    telefone?: string;
  };
  dadosOab?: {
    nomeAdvogado?: string;
    numeroOab?: string;
  };
}

interface ExecucaoComDetalhes {
  id: string;
  numeroProcesso?: string | null;
  numeroProtocolo?: string | null;
  titulo?: string;
  descricao?: string;
  valorCausa?: number | string | null;
  status?: string;
  observacoes?: string;
  grau?: string | null;
  prioridade?: string | null;
  segredoJustica?: boolean | null;
  justicaGratuita?: boolean | null;
  partes?: ParteExecucao[];
  tribunal?: {
    nome: string;
    urlBase?: string;
  } | null;
  comarca?: {
    nome: string;
  } | null;
  classeJudicial?: {
    nome: string;
  } | null;
  dadosProcesso?: {
    etapa?: string;
    tipoProcesso?: string;
    dadosAdicionais?: {
      grau?: string;
      ritoProcesso?: string;
      areaDireito?: string;
      tipoCitacao?: string;
      valorCausa?: string;
      assunto?: string;
      observacoes?: string;
      segredoJustica?: string | boolean;
      justicaGratuita?: string | boolean;
      prioridade?: string;
      [key: string]: unknown;
    };
  } | null;
  dataCriacao?: string | Record<string, never> | null;
  dataAtualizacao?: string | Record<string, never> | null;
}

class CumprimentoService {
  async buscarPorId(id: string): Promise<CumprimentoDetalhes> {
    const response = await apiGet(`/processos/${id}`);
    const result = await response.json();
    const execucao = result.data as ExecucaoComDetalhes;

    if (!execucao) {
      throw new Error('Dados da execução não encontrados');
    }

    return this.mapExecucaoParaCumprimento(execucao);
  }

  async listarAtividades(
    execucaoId: string,
    filtros?: {
      dataInicio?: string;
      dataFim?: string;
      tipoAtividade?: string;
      usuarioId?: string;
      limit?: number;
      offset?: number;
    },
  ): Promise<AtividadeRecente[]> {
    const queryParams = new URLSearchParams();
    if (filtros?.dataInicio) queryParams.append('dataInicio', filtros.dataInicio);
    if (filtros?.dataFim) queryParams.append('dataFim', filtros.dataFim);
    if (filtros?.tipoAtividade) queryParams.append('tipoAtividade', filtros.tipoAtividade);
    if (filtros?.usuarioId) queryParams.append('usuarioId', filtros.usuarioId);
    if (filtros?.limit) queryParams.append('limit', filtros.limit.toString());
    if (filtros?.offset) queryParams.append('offset', filtros.offset.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/processos/${execucaoId}/atividades?${queryString}`
      : `/processos/${execucaoId}/atividades`;

    const response = await apiGet(url);
    const result = await response.json();
    const data = result.data as AtividadeBackend[];

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((atividade) => ({
      id: atividade.id,
      usuario: atividade.usuario?.nomeCompleto || 'Sistema',
      acao: this.mapTipoAtividadeParaAcao(atividade.tipoAtividade),
      data: atividade.dataAtividade,
      detalhes: atividade.descricao,
      tipo: this.mapTipoAtividadeParaTipo(atividade.tipoAtividade),
      origem: 'equipe' as const,
      anexos: [],
    }));
  }

  async buscarTimeline(execucaoId: string): Promise<TimelineItemData[]> {
    const response = await apiGet(`/processos/${execucaoId}/atividades/timeline`);
    const result = await response.json();
    const data = result.data as AtividadeBackend[];

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((atividade) => ({
      id: atividade.id,
      title: this.mapTipoAtividadeParaTitulo(atividade.tipoAtividade),
      description: atividade.descricao,
      status: this.mapTipoAtividadeParaStatus(atividade.tipoAtividade),
      icon: this.mapTipoAtividadeParaIcon(atividade.tipoAtividade),
      startDate: atividade.dataAtividade,
      completedDate: atividade.dataAtividade,
      badges: [],
    }));
  }

  async atualizarStatus(id: string, dados: AtualizarStatusDto): Promise<CumprimentoDetalhes> {
    const response = await apiPut(`/processos/${id}`, dados);
    const result = await response.json();
    const data = result.data as ExecucaoComDetalhes;

    if (!data) {
      throw new Error('Dados da execução não encontrados após atualização');
    }

    return this.mapExecucaoParaCumprimento(data);
  }

  async arquivar(id: string, dados: ArquivarCumprimentoDto): Promise<void> {
    await apiPut(`/execucoes/${id}/arquivar`, dados);
  }

  async listarAnotacoes(execucaoId: string): Promise<Anotacao[]> {
    const response = await apiGet(`/processos/${execucaoId}/anotacoes`);
    const result = await response.json();
    const data = (Array.isArray(result) ? result : result.data) as AnotacaoBackend[];

    if (!data || !Array.isArray(data)) {
      return [];
    }

    return data.map((anotacao) => ({
      id: anotacao.id,
      texto: anotacao.texto,
      prazo: anotacao.prazo,
      responsavel: anotacao.responsaveis,
      andamento: anotacao.andamento,
      dataCreacao: anotacao.dataCriacao,
      dataEdicao: anotacao.dataAtualizacao,
      autor: anotacao.usuario?.nomeCompleto || 'Usuário',
      status: anotacao.status.toLowerCase() as 'ativa' | 'concluida' | 'cancelada',
      prioridade: anotacao.prioridade?.toLowerCase() as 'alta' | 'media' | 'baixa' | undefined,
    }));
  }

  async criarAnotacao(execucaoId: string, dados: CriarAnotacaoDto): Promise<Anotacao> {
    const payload = {
      texto: dados.texto,
      prazo: dados.prazo,
      responsaveis: dados.responsavel || [],
      andamento: dados.andamento,
      prioridade: dados.prioridade?.toUpperCase(),
    };

    const response = await apiPost(`/processos/${execucaoId}/anotacoes`, payload);
    const result = await response.json();
    const data = result.data as AnotacaoBackend;

    return {
      id: data.id,
      texto: data.texto,
      prazo: data.prazo,
      responsavel: data.responsaveis,
      andamento: data.andamento,
      dataCreacao: data.dataCriacao,
      dataEdicao: data.dataAtualizacao,
      autor: data.usuario?.nomeCompleto || 'Usuário',
      status: data.status.toLowerCase() as 'ativa' | 'concluida' | 'cancelada',
      prioridade: data.prioridade?.toLowerCase() as 'alta' | 'media' | 'baixa' | undefined,
    };
  }

  async atualizarAnotacao(anotacaoId: string, dados: AtualizarAnotacaoDto): Promise<Anotacao> {
    const payload = {
      texto: dados.texto,
      prazo: dados.prazo,
      responsaveis: dados.responsavel,
      andamento: dados.andamento,
      status: dados.status?.toUpperCase(),
      prioridade: dados.prioridade?.toUpperCase(),
    };

    const response = await apiPut(`/anotacoes/${anotacaoId}`, payload);
    const result = await response.json();
    const data = result.data as AnotacaoBackend;

    return {
      id: data.id,
      texto: data.texto,
      prazo: data.prazo,
      responsavel: data.responsaveis,
      andamento: data.andamento,
      dataCreacao: data.dataCriacao,
      dataEdicao: data.dataAtualizacao,
      autor: data.usuario?.nomeCompleto || 'Usuário',
      status: data.status.toLowerCase() as 'ativa' | 'concluida' | 'cancelada',
      prioridade: data.prioridade?.toLowerCase() as 'alta' | 'media' | 'baixa' | undefined,
    };
  }

  async excluirAnotacao(anotacaoId: string): Promise<void> {
    await apiDelete(`/anotacoes/${anotacaoId}`);
  }

  private mapTipoAtividadeParaAcao(tipo: string): string {
    const map: Record<string, string> = {
      PETICAO_PROTOCOLADA: 'Petição protocolada',
      CITACAO_REALIZADA: 'Citação realizada',
      CONTESTACAO_APRESENTADA: 'Contestação apresentada',
      AUDIENCIA_REALIZADA: 'Audiência realizada',
      SENTENCA_PROFERIDA: 'Sentença proferida',
      RECURSO_INTERPOSTO: 'Recurso interposto',
      TRANSITO_JULGADO: 'Trânsito em julgado',
      CUMPRIMENTO_INICIADO: 'Cumprimento iniciado',
      PENHORA_REALIZADA: 'Penhora realizada',
      ACORDO_HOMOLOGADO: 'Acordo homologado',
      PROCESSO_ARQUIVADO: 'Processo arquivado',
      STATUS_ALTERADO: 'Status alterado',
      DOCUMENTO_ADICIONADO: 'Documento adicionado',
      PRAZO_VENCIDO: 'Prazo vencido',
    };
    return map[tipo] || tipo;
  }

  private mapTipoAtividadeParaTipo(tipo: string): 'documento' | 'acao' | 'status' | 'comentario' {
    if (tipo.includes('DOCUMENTO')) return 'documento';
    if (tipo.includes('STATUS')) return 'status';
    return 'acao';
  }

  private mapTipoAtividadeParaTitulo(tipo: string): string {
    return this.mapTipoAtividadeParaAcao(tipo);
  }

  private mapTipoAtividadeParaStatus(tipo: string): 'completed' | 'current' | 'pending' {
    const completados = [
      'SENTENCA_PROFERIDA',
      'TRANSITO_JULGADO',
      'ACORDO_HOMOLOGADO',
      'PROCESSO_ARQUIVADO',
    ];
    return completados.includes(tipo) ? 'completed' : 'current';
  }

  private mapTipoAtividadeParaIcon(tipo: string): string {
    const iconMap: Record<string, string> = {
      PETICAO_PROTOCOLADA: 'file-text',
      CITACAO_REALIZADA: 'mail',
      CONTESTACAO_APRESENTADA: 'message-square',
      AUDIENCIA_REALIZADA: 'users',
      SENTENCA_PROFERIDA: 'gavel',
      RECURSO_INTERPOSTO: 'arrow-up',
      TRANSITO_JULGADO: 'check-circle',
      CUMPRIMENTO_INICIADO: 'play',
      PENHORA_REALIZADA: 'lock',
      ACORDO_HOMOLOGADO: 'handshake',
      PROCESSO_ARQUIVADO: 'archive',
      STATUS_ALTERADO: 'refresh-cw',
      DOCUMENTO_ADICIONADO: 'file-plus',
      PRAZO_VENCIDO: 'alert-circle',
    };
    return iconMap[tipo] || 'circle';
  }

  private mapExecucaoParaCumprimento(execucao: ExecucaoComDetalhes): CumprimentoDetalhes {
    if (!execucao) {
      throw new Error('Execução inválida para mapeamento');
    }

    const partes = execucao?.partes || [];
    const autores = partes.filter((p) => p.tipoParte === 'AUTOR');
    const reus = partes.filter((p) => p.tipoParte === 'REU');

    const autor1 = autores[0];
    const autor2 = autores[1];
    const reu1 = reus[0];
    const reu2 = reus[1];

    // Tenta pegar valorCausa do raiz ou do snapshot dadosAdicionais
    const valorRaw = execucao.valorCausa || execucao.dadosProcesso?.dadosAdicionais?.valorCausa;

    const valorNum =
      typeof valorRaw === 'string'
        ? parseFloat(valorRaw.replace(/[^\d,.-]/g, '').replace(',', '.'))
        : Number(valorRaw || 0);

    const valorFormatado = valorNum > 0 ? formatCurrency(valorNum) : 'R$ 0,00';

    const dadosAdicionais = execucao.dadosProcesso?.dadosAdicionais;

    return {
      id: execucao.id,
      numeroProcesso: execucao.numeroProtocolo || execucao.numeroProcesso || '',
      parteRequerente: autor1?.pessoa?.nomeCompleto || autor1?.nomeCompleto || '',
      parteRequerente2: autor2?.pessoa?.nomeCompleto || autor2?.nomeCompleto,
      parteRequerida: reu1?.pessoa?.nomeCompleto || reu1?.nomeCompleto || '',
      parteRequerida2: reu2?.pessoa?.nomeCompleto || reu2?.nomeCompleto,
      cpfCnpjRequerente:
        autor1?.pessoa?.cpfCnpj || autor1?.documentoPrincipal || autor1?.documentoFormatado || '',
      emailRequerente: autor1?.pessoa?.email || autor1?.dadosContato?.email || '',
      telefoneRequerente: autor1?.pessoa?.telefone || autor1?.dadosContato?.telefone || '',
      cpfCnpjRequerida:
        reu1?.pessoa?.cpfCnpj || reu1?.documentoPrincipal || reu1?.documentoFormatado || '',
      emailRequerida: reu1?.pessoa?.email || reu1?.dadosContato?.email || '',
      telefoneRequerida: reu1?.pessoa?.telefone || reu1?.dadosContato?.telefone || '',
      cpfCnpjRequerida2:
        reu2?.pessoa?.cpfCnpj || reu2?.documentoPrincipal || reu2?.documentoFormatado,
      emailRequerida2: reu2?.pessoa?.email || reu2?.dadosContato?.email,
      telefoneRequerida2: reu2?.pessoa?.telefone || reu2?.dadosContato?.telefone,
      advogadoRequerente: autor1?.dadosOab?.nomeAdvogado || '',
      advogadoRequerido: reu1?.dadosOab?.nomeAdvogado || '',
      resumoProcesso: execucao.classeJudicial?.nome || execucao.descricao || '',
      valor: valorFormatado,
      status: this.mapStatusExecucao(execucao.status || ''),
      statusTribunal: execucao.status || 'Em andamento',
      dataInicio: this.formatarData(execucao.dataCriacao),
      dataUltimaAtualizacao: this.formatarData(execucao.dataAtualizacao),
      observacoes: execucao.observacoes || dadosAdicionais?.observacoes || '',
      tribunal: execucao.tribunal?.nome || '',
      comarca: execucao.comarca?.nome || '',
      linkTribunal: execucao.tribunal?.urlBase || '',
      grau: (dadosAdicionais?.grau || execucao.grau) ?? undefined,
      rito: dadosAdicionais?.ritoProcesso ?? undefined,
      areaDireito: dadosAdicionais?.areaDireito ?? undefined,
      tipoCitacao: dadosAdicionais?.tipoCitacao ?? undefined,
      classeJudicial: execucao.classeJudicial?.nome ?? undefined,
      assunto: dadosAdicionais?.assunto ?? undefined,
      prioridade: (execucao.prioridade || (dadosAdicionais?.prioridade as string)) ?? undefined,
      segredoJustica:
        execucao.segredoJustica ?? (dadosAdicionais?.segredoJustica === 'sim' || undefined),
      justicaGratuita:
        execucao.justicaGratuita ?? (dadosAdicionais?.justicaGratuita === 'sim' || undefined),
    };
  }

  private formatarData(data: string | Record<string, never> | null | undefined): string {
    return formatDate(data);
  }

  private mapStatusExecucao(status: string): StatusCumprimento {
    const statusMap: Record<string, StatusCumprimento> = {
      RASCUNHO: 'Em Análise',
      PROTOCOLADO: 'Distribuído',
      EM_ANDAMENTO: 'Distribuído',
      CONCLUIDO: 'Concluído',
      ARQUIVADO: 'Arquivado',
    };
    return statusMap[status] || 'Elegível';
  }
}

export const cumprimentoService = new CumprimentoService();
